class ProductFlavorSelector extends HTMLElement {
  constructor() {
    super();
    /**
     * DOM Elements
     */
    this.checkboxes = this.querySelectorAll("[data-flavor-checkbox]");
    this.instructions = this.querySelector("[data-flavor-instructions]");
    this.errorElement = this.querySelector("[data-flavor-error]");

    /**
     * Rules & Options
     * - rules: Array of parsed variant rules that determine max flavor selections
     * - optionTypes: Set of variant types we need to track (e.g., "Size", "Type")
     */
    this.rules = this.parseRules(this.dataset.variantRules);
    this.optionTypes = new Set();
    this.rules.forEach((rule) => {
      rule.conditions.forEach((condition) => {
        this.optionTypes.add(condition.option);
      });
    });

    /**
     * State Management
     * - selectedOptions: Tracks current variant selections (e.g., { Size: "50 pcs", Type: "Gift Box" })
     *   Used to determine which rule applies and therefore how many flavors can be selected
     *
     * - selectionQueue: Maintains FIFO order of flavor checkbox selections
     *   When max selections is reached, oldest selection is removed first
     *   Example: If max=2 and user selects [A,B,C], queue will be [B,C] (A was removed)
     */
    this.selectedOptions = {};
    this.selectionQueue = [];
    this.maxSelections = 1; // Default
    this.minSelections = 1; // Always require at least 1

    this.instructionTemplate = this.dataset.instructionTemplate;
    this.errorTooMany = this.dataset.errorTooMany || "You've selected too many flavors. Maximum {max} allowed.";
    this.errorTooFew = this.dataset.errorTooFew || "Please complete your flavor selection ({min} required).";

    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", () => this.initialize());
    } else {
      this.initialize();
    }
  }

  initialize() {
    // Initialize variant selectors
    this.initializeVariantSelectors();
    
    // Set up variant change listeners
    this.setupVariantListeners();
    
    // Initialize max selections based on current variant
    this.updateSelectionsBasedOnVariant();
    
    // Set up flavor checkbox listeners
    this.checkboxes.forEach((checkbox) => {
      checkbox.addEventListener("change", this.handleFlavorChange.bind(this));
    });

    // Ensure at least one flavor is selected by default
    this.ensureMinimumSelection();

    // Load the flavor → ingredients map from the embedded JSON script tag
    this.loadIngredientsMap();

    // Paint the ingredients section once on load to match the default flavor
    this.updateIngredientsDisplay();

    // Intercept form submission
    this.setupFormSubmission();
  }

  /**
   * Read the flavor → ingredient HTML map emitted by
   * snippets/product-flavor-selector.liquid. Stored as JSON inside a
   * <script type="application/json" data-flavor-ingredients-map> tag.
   * Each value is the rendered HTML of the flavor product's ingredients
   * metafield (custom.cherry_oreo_ingredients on each flavor product).
   */
  loadIngredientsMap() {
    this.ingredientsMap = {};
    const mapEl = this.querySelector("script[data-flavor-ingredients-map]");
    if (!mapEl) return;
    try {
      this.ingredientsMap = JSON.parse(mapEl.textContent) || {};
    } catch (err) {
      console.warn("[product-flavor-selector] could not parse ingredients map:", err);
    }
  }

  /**
   * Find the `[data-flavor-ingredients-target]` container (sits inside the
   * "All Ingredients" expandable block, populated server-side by
   * templates/product.json) and inject the matching flavor's ingredient
   * HTML based on the current selection.
   *
   * - Single selection → just inject that flavor's ingredients.
   * - Multi selection (e.g. 50 pcs allows 2 flavors) → stack each, headed
   *   by the flavor name so the customer can read them in order.
   * - Empty selection → fallback message.
   * - Missing data for a flavor → soft fallback so the section never goes
   *   blank without explanation.
   */
  /**
   * Find the "All Ingredients" expandable block's content div.
   * Shopify's richtext setting strips custom data-* and id attributes,
   * so we can't put a marker on the target via templates/product.json.
   * Instead, walk the rendered disclosure summaries and match by title
   * text — this is the only disclosure on the Minis PDP titled
   * "All Ingredients", per templates/product.json's expandable block.
   */
  findIngredientsTarget() {
    const summaries = document.querySelectorAll(".disclosure__summary");
    for (const summary of summaries) {
      if (summary.textContent.trim() === "All Ingredients") {
        return summary.parentElement.querySelector(".disclosure__content");
      }
    }
    return null;
  }

  updateIngredientsDisplay() {
    const target = this.findIngredientsTarget();
    if (!target) return;
    if (!this.ingredientsMap) this.ingredientsMap = {};

    const selectedFlavors = Array.from(this.checkboxes)
      .filter((cb) => cb.checked)
      .map((cb) => cb.value);

    if (selectedFlavors.length === 0) {
      target.innerHTML = "<p>Select a flavor above to see ingredients.</p>";
      return;
    }

    const html = selectedFlavors
      .map((flavor) => {
        const ingredients = this.ingredientsMap[flavor];
        const heading = selectedFlavors.length > 1
          ? `<p><strong>${flavor}</strong></p>`
          : "";
        if (!ingredients) {
          return `${heading}<p><em>Ingredients for ${flavor} are not available yet.</em></p>`;
        }
        // Ingredient metafield values are plain strings; wrap in <p> so the
        // disclosure content is valid HTML.
        return `${heading}<p>${ingredients}</p>`;
      })
      .join("");

    target.innerHTML = html;
  }

  initializeVariantSelectors() {
    this.variantSelectors = {};
    this.optionTypes.forEach((optionType) => {
      const selector = document.querySelector(
        `input[type="radio"][name*="${optionType}"]:checked`
      ) || document.querySelector(
        `input[type="radio"][name*="${optionType}"]`
      );
      
      if (selector) {
        if (!selector.checked) selector.checked = true;
        this.variantSelectors[optionType] = selector;
        this.selectedOptions[optionType] = selector.dataset.optionValue;
      }
    });
  }

  setupVariantListeners() {
    document.querySelectorAll('input[type="radio"]').forEach((radio) => {
      const optionType = Array.from(this.optionTypes).find((type) =>
        radio.name.includes(type)
      );

      if (optionType) {
        radio.addEventListener("change", (event) => {
          this.selectedOptions[optionType] = event.target.dataset.optionValue;
          this.updateSelectionsBasedOnVariant();
        });
      }
    });
  }

  setupFormSubmission() {
    const form = document.querySelector('form[data-type="add-to-cart-form"]');
    if (!form || form.dataset.flavorSubmitBound === "true") return;
    form.dataset.flavorSubmitBound = "true";
    this.cartForm = form;

    // Keep the hidden flavor input current so product-form.js's own AJAX FormData
    // picks it up. Do NOT cloneNode/replaceChild the form: cloneNode does not copy
    // event listeners, so replacing the form strips product-form's submit handler —
    // which drops the AJAX cart-drawer add AND the gift-config interception, forcing
    // a native full-page POST. (Verified HIGH bug, theme buy-flow hunt 2026-06-25.)
    this.syncFlavorInput();

    // Capture phase: an invalid selection blocks product-form's (bubble-phase) AJAX
    // add via stopImmediatePropagation; on a valid selection we re-sync the flavor
    // input and let product-form's own submit handler run intact.
    form.addEventListener(
      "submit",
      (event) => {
        const validation = this.validateSelection();
        if (!validation.isValid) {
          event.preventDefault();
          event.stopImmediatePropagation();
          this.showError(validation.error);
          return;
        }
        this.syncFlavorInput();
        this.hideError();
      },
      true,
    );
  }

  // Upsert the hidden properties[Flavor] input on the add-to-cart form with the
  // current selection, so it's already in product-form's FormData at submit time.
  syncFlavorInput() {
    const form = this.cartForm || document.querySelector('form[data-type="add-to-cart-form"]');
    if (!form) return;
    const flavors = Array.from(this.checkboxes).filter((cb) => cb.checked).map((cb) => cb.value);
    let input = form.querySelector('input[name="properties[Flavor]"]');
    if (!flavors.length) {
      if (input) input.remove();
      return;
    }
    if (!input) {
      input = document.createElement("input");
      input.type = "hidden";
      input.name = "properties[Flavor]";
      form.appendChild(input);
    }
    input.value = flavors.join(", ");
  }

  parseRules(rulesText) {
    if (!rulesText) return [];
    
    return rulesText.split("\n").map((line) => {
      const [conditionsPart, countPart] = line.split(":");
      const conditions = conditionsPart.split(",").map((condition) => {
        const match = condition.match(/(\w+)\[(.*?)\]/);
        if (!match) return null;
        return { option: match[1].trim(), value: match[2].trim() };
      }).filter(Boolean);
      
      // Guard a malformed rule line (missing "=", non-numeric, <= 0). Without this a
      // NaN count flows to maxSelections, and `length > NaN` is always false, so max-
      // selection enforcement silently breaks (a customer could pick unlimited flavors
      // on a fixed-count box). Fail safe to 1 (the minimum) instead.
      const parsed = parseInt((countPart || "").split("=")[1], 10);
      const count = Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
      return { conditions, count };
    });
  }

  updateSelectionsBasedOnVariant() {
    // Find matching rule for current variant
    const matchingRule = this.rules.find((rule) => {
      return rule.conditions.every((condition) => {
        return this.selectedOptions[condition.option] === condition.value;
      });
    });

    this.maxSelections = matchingRule ? matchingRule.count : 1;

    // Update UI
    const maxCountElement = this.querySelector("[data-max-count]");
    if (maxCountElement) {
      maxCountElement.textContent = this.maxSelections;
    }

    // Update instructions
    if (this.instructionTemplate) {
      const variantText = Object.values(this.selectedOptions).filter(Boolean).join(" - ");
      const instruction = this.instructionTemplate
        .replace("{count}", this.maxSelections)
        .replace("{variant}", variantText);
      if (this.instructions) {
        this.instructions.textContent = instruction;
      }
    }

    // Enforce max selections on existing checkboxes
    this.enforceMaxSelections();
  }

  handleFlavorChange(event) {
    if (event.target.checked) {
      // Add to queue
      this.selectionQueue.push(event.target);
      
      // Enforce max selections
      if (this.selectionQueue.length > this.maxSelections) {
        const removed = this.selectionQueue.shift();
        removed.checked = false;
      }
    } else {
      // Remove from queue
      this.selectionQueue = this.selectionQueue.filter(cb => cb !== event.target);
    }
    
    // Clear any errors when user is actively selecting
    this.hideError();

    // Refresh the "All Ingredients" section to match the new selection
    this.updateIngredientsDisplay();

    // Keep the hidden flavor input in sync so it's current in product-form's FormData.
    this.syncFlavorInput();

    // Track flavor selection
    if (window.SnowcubesAnalytics) {
      window.SnowcubesAnalytics.track(
        window.SnowcubesAnalytics.EVENTS.FLAVOR_SELECT,
        {
          selected_flavors: this.selectionQueue.map(cb => cb.value),
          max_selections: this.maxSelections,
          action: event.target.checked ? 'add' : 'remove',
          flavor: event.target.value,
        }
      );
    }
  }

  enforceMaxSelections() {
    const currentlyChecked = Array.from(this.checkboxes).filter(cb => cb.checked);
    
    if (currentlyChecked.length > this.maxSelections) {
      // Keep only the most recent selections
      const toUncheck = currentlyChecked.length - this.maxSelections;
      for (let i = 0; i < toUncheck; i++) {
        currentlyChecked[i].checked = false;
      }
      
      // Update selection queue
      this.selectionQueue = Array.from(this.checkboxes).filter(cb => cb.checked);
    }
  }

  ensureMinimumSelection() {
    const hasSelection = Array.from(this.checkboxes).some(cb => cb.checked);
    
    if (!hasSelection && this.checkboxes.length > 0) {
      this.checkboxes[0].checked = true;
      this.selectionQueue = [this.checkboxes[0]];
    } else {
      // Initialize queue with existing selections
      this.selectionQueue = Array.from(this.checkboxes).filter(cb => cb.checked);
    }
  }

  validateSelection() {
    const selected = Array.from(this.checkboxes).filter(cb => cb.checked);
    const selectedFlavors = selected.map(cb => cb.value);
    
    // Must have at least one flavor
    if (selected.length === 0) {
      return {
        isValid: false,
        error: "Please select at least one flavor"
      };
    }
    
    // Can't have more than max
    if (selected.length > this.maxSelections) {
      return {
        isValid: false,
        error: this.errorTooMany.replace("{max}", this.maxSelections)
      };
    }
    
    // For now, we allow any number from 1 to max (flexible selection)
    // If you need exact selection (must be exactly max), uncomment below:
    /*
    if (selected.length !== this.maxSelections && this.maxSelections > 1) {
      return {
        isValid: false,
        error: `Please select exactly ${this.maxSelections} flavors`
      };
    }
    */
    
    return {
      isValid: true,
      flavors: selectedFlavors
    };
  }

  showError(message) {
    if (this.errorElement) {
      const errorMessage = this.errorElement.querySelector(".error-message");
      if (errorMessage) {
        errorMessage.textContent = message;
      }
      this.errorElement.style.display = "block";
    }
  }

  hideError() {
    if (this.errorElement) {
      this.errorElement.style.display = "none";
    }
  }
}

customElements.define("product-flavor-selector", ProductFlavorSelector);
